import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Sparkles, ShieldAlert, CheckCircle2, ArrowRight, FileText, Zap,
} from "lucide-react";
import { launchPreview } from "../api";

/**
 * Public homepage-preview viewer at /launch/preview/:token.
 * MVP renders a single stylised hero + page-plan + brief summary + fact-
 * verification prompt. Watermarked "PREVIEW · Not Live" to enforce the
 * non-negotiable that this is not a public production launch.
 */
export default function LaunchPreviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Poll for up to ~30s while AI analysis completes.
      for (let i = 0; i < 15; i++) {
        try {
          const d = await launchPreview(token);
          if (!alive) return;
          setData(d);
          if (d.preview) return;
        } catch (e) {
          if (!alive) return;
          setErr(e?.response?.data?.detail || "Preview not found.");
          return;
        }
        await new Promise(r => setTimeout(r, 2000));
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="p-8 max-w-md text-center">
        <ShieldAlert className="mx-auto text-rose-500" size={28} />
        <div className="text-lg font-bold text-slate-900 mt-3">Preview not found</div>
        <div className="text-sm text-slate-500 mt-1">{err}</div>
      </Card>
    </div>
  );
  if (!data?.preview) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="preview-loading">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-sky-600" size={30} />
        <div className="mt-3 text-sm text-slate-600">Our AI is drafting your preview…</div>
      </div>
    </div>
  );

  const { preview, brief, business_name, vertical } = data;
  const accent = preview.accent_color || "#0f172a";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="launch-preview-page">
      {/* Watermark banner */}
      <div className="sticky top-0 z-30 bg-amber-400 text-slate-900 text-center py-2 text-xs font-bold tracking-wide"
           data-testid="preview-watermark">
        <Sparkles size={12} className="inline mr-1 -mt-0.5" /> PREVIEW · NOT LIVE — verify every fact before this can go public.
      </div>

      {/* Hero mock */}
      <section className="relative overflow-hidden" style={{ backgroundColor: accent }}>
        <div className="absolute inset-0 opacity-15"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,.4), transparent 55%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-24 text-white">
          <Badge className="bg-white/15 border border-white/20 uppercase tracking-widest text-[10px] font-bold">
            {vertical.replace("_", " ")}
          </Badge>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black leading-tight max-w-3xl"
              data-testid="preview-headline">
            {preview.headline}
          </h1>
          <p className="mt-4 text-lg opacity-90 max-w-2xl" data-testid="preview-subheadline">
            {preview.subheadline}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button className="bg-white text-slate-900 hover:bg-slate-100 font-bold" data-testid="preview-cta">
              {preview.primary_cta} <ArrowRight size={16} className="ml-1" />
            </Button>
            <span className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-black/25 text-white/90">
              <ShieldAlert size={12} /> This button is disabled — preview only
            </span>
          </div>
          <div className="mt-8 text-xs opacity-70">{preview.hero_note}</div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-6">
        {/* Page plan */}
        <Card className="p-6 bg-white lg:col-span-2" data-testid="preview-page-plan">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <FileText size={12} /> Suggested pages
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {(preview.page_plan || []).map((p, i) => (
              <div key={p} className="rounded-lg border border-slate-200 p-4 flex items-center gap-3"
                   data-testid={`page-plan-${i}`}>
                <div className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-white"
                     style={{ backgroundColor: accent }}>{i + 1}</div>
                <div>
                  <div className="font-semibold text-slate-900">{p}</div>
                  <div className="text-xs text-slate-500">In the starter (up to 5 pages)</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Brief summary */}
        <Card className="p-6 bg-white" data-testid="preview-brief">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <Zap size={12} /> Website brief (AI draft — verify before launch)
          </div>
          <div className="mt-3 space-y-3 text-sm">
            <Row label="Business" value={business_name} />
            <Row label="Target audience" value={brief?.target_customers} />
            <Row label="Primary goal" value={brief?.primary_goal} />
            <Row label="Tone of voice" value={brief?.tone} />
            {brief?.missing_facts?.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mt-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Missing information</div>
                <ul className="mt-1 list-disc list-inside text-xs text-amber-900">
                  {brief.missing_facts.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-12">
        <Card className="p-6 bg-emerald-50 border-emerald-200">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-600 mt-1" size={22} />
            <div>
              <div className="font-bold text-emerald-900">You're qualified for the Starter Launch — no design fee.</div>
              <div className="text-sm text-emerald-900/80 mt-1">
                Next, verify each fact, pick a design direction, and choose an eligible hosting & care plan.
                We'll build your 5 pages, run automated QA, and a human reviewer approves the launch.
              </div>
              <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="preview-next-step">
                See plans (coming in Phase 2)
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-slate-800">{value}</div>
    </div>
  );
}
